<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Vulnerabilities</h1>
      <div class="flex items-center gap-2">
        <button @click="exportVDR" class="btn-secondary" title="Download CycloneDX VEX/VDR of public vulnerabilities">
          Export VDR
        </button>
        <button
          @click="showModal = true"
          class="btn-primary"
        >
          Add Vulnerability
        </button>
      </div>
    </div>

    <DataTable
      :columns="columns"
      resourceName="vulnerabilities"
      @rowClick="showVulnerabilityDetails"
    >
      <template #cell="{ column, value, row }">
        <template v-if="column.key === 'severity'">
          <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
            :class="{
              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': value === 'CRITICAL',
              'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200': value === 'HIGH',
              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': value === 'MEDIUM',
              'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': value === 'LOW'
            }"
          >
            {{ value }}
          </div>
        </template>
        <template v-else-if="column.key === 'status'">
          <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            :class="{
              'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': value === 'FIXED',
              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': value === 'IN_PROGRESS',
              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': value === 'OPEN'
            }"
          >
            {{ value }}
          </div>
        </template>
        <template v-else-if="column.key === 'due_by'">
          <span v-if="!value" class="text-xs text-gray-400 dark:text-gray-500">—</span>
          <span
            v-else-if="row.status === 'FIXED'"
            class="text-xs text-gray-500 dark:text-gray-400"
            :title="`Fixed ${row.remediated_at ? formatDate(row.remediated_at) : ''}`"
          >{{ formatDate(value) }}</span>
          <span
            v-else
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            :class="isDatePast(value)
              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              : daysUntil(value) <= 7
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'"
            :title="isDatePast(value) ? 'Overdue' : `${daysUntil(value)}d remaining`"
          >{{ formatDate(value) }}</span>
        </template>
        <template v-else-if="column.key === 'advisory_status'">
          <span v-if="!value" class="text-xs text-gray-400 dark:text-gray-500">—</span>
          <span
            v-else
            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
            :class="{
              'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200': value === 'draft',
              'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': value === 'scheduled',
              'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': value === 'published'
            }"
          >{{ value }}</span>
        </template>
      </template>
    </DataTable>

    <!-- Add/Edit Vulnerability Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" @click.self="closeModal">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-start mb-6">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ selectedVulnerability ? 'Edit Vulnerability' : 'Add Vulnerability' }}
            </h2>
            <button 
              @click="closeModal" 
              class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
            >
              <span class="sr-only">Close</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Public/Private toggle -->
          <div class="mb-4 p-3 rounded-lg border" :class="form.is_public ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700/50'">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium" :class="form.is_public ? 'text-green-800 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'">
                {{ form.is_public ? 'Public — visible on trust center' : 'Private — internal only' }}
              </span>
              <button type="button" @click="selectedVulnerability ? toggleVulnVisibility() : (form.is_public = !form.is_public)"
                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                :class="form.is_public ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'">
                <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                  :class="form.is_public ? 'translate-x-6' : 'translate-x-1'"></span>
              </button>
            </div>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Product</label>
              <select
                v-model="form.product_id"
                @change="onProductChange"
                class="input mt-1"
              >
                <option :value="null">No product (tenant-wide)</option>
                <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}{{ p.version ? ` (${p.version})` : '' }}</option>
              </select>
            </div>

            <div v-if="form.product_id && availableVersions.length > 0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Affected Versions</label>
              <div class="mt-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 max-h-40 overflow-y-auto">
                <input
                  v-if="availableVersions.length > 5"
                  v-model="versionSearch"
                  type="text"
                  placeholder="Search versions..."
                  class="w-full px-3 py-1.5 text-sm border-b border-gray-200 dark:border-gray-600 bg-transparent dark:text-white focus:outline-none"
                />
                <label
                  v-for="v in filteredVersions" :key="v.id"
                  class="flex items-center px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                >
                  <input type="checkbox" :value="v.id" v-model="form.affected_version_ids"
                    class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600" />
                  <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">{{ v.version }}</span>
                </label>
                <p v-if="filteredVersions.length === 0" class="px-3 py-2 text-xs text-gray-400">No matching versions</p>
              </div>
              <div class="flex gap-2 mt-1">
                <button type="button" @click="form.affected_version_ids = availableVersions.map(v => v.id)" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">Select all</button>
                <button type="button" @click="form.affected_version_ids = []" class="text-xs text-gray-500 dark:text-gray-400 hover:underline">Clear</button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CVE ID <span class="text-gray-400 font-normal">(optional)</span></label>
              <input
                v-model="form.cve_id"
                type="text"
                placeholder="e.g. CVE-2025-12345"
                class="input mt-1"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Synopsis</label>
              <input
                v-model="form.synopsis"
                type="text"
                required
                class="input mt-1"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                v-model="form.description"
                rows="3"
                required
                class="input mt-1"
              ></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Severity</label>
              <select
                v-model="form.severity"
                required
                class="input mt-1"
              >
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <select
                v-model="form.status"
                required
                class="input mt-1"
              >
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="FIXED">Fixed</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Fixed Version</label>
              <input
                v-model="form.fixed_version"
                type="text"
                class="input mt-1"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Remediation Due By
              </label>
              <input
                v-model="form.due_by"
                type="date"
                class="input mt-1"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Leave blank to auto-compute from severity ({{ slaDefaultDays }} days for {{ form.severity }}).
              </p>
              <p v-if="form.due_by && form.status !== 'FIXED' && isDatePast(form.due_by)" class="mt-1 text-xs text-red-600 dark:text-red-400">
                Overdue since {{ formatDate(form.due_by) }}
              </p>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Advisory State</label>
                <select v-model="form.advisory_status" class="input mt-1">
                  <option value="">Internal (no advisory)</option>
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="published">Published</option>
                </select>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Scheduled advisories auto-publish at the chosen time.
                </p>
              </div>
              <div v-if="form.advisory_status === 'scheduled'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Publish At</label>
                <input
                  v-model="form.publish_at"
                  type="datetime-local"
                  class="input mt-1"
                />
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CWE</label>
                <input
                  v-model="form.cwe"
                  type="text"
                  placeholder="e.g. CWE-79"
                  class="input mt-1"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CVSS Score</label>
                <input
                  v-model.number="form.cvss_score"
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  placeholder="0.0 - 10.0"
                  class="input mt-1"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CVSS Vector</label>
              <input
                v-model="form.cvss"
                type="text"
                placeholder="e.g. CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H"
                class="input mt-1"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Affected Components</label>
              <input
                v-model="form.affected_components"
                type="text"
                placeholder="Comma-separated package URLs"
                class="input mt-1"
              />
            </div>

            <div class="flex justify-end space-x-3 mt-6">
              <button
                v-if="selectedVulnerability"
                type="button"
                @click="reportToENISA(selectedVulnerability)"
                class="px-4 py-2 border border-orange-300 dark:border-orange-600 rounded-md text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                :disabled="isSaving || isReporting"
              >
                {{ isReporting ? 'Creating...' : 'Report to ENISA' }}
              </button>
              <button
                v-if="selectedVulnerability"
                type="button"
                @click="deleteVulnerability(selectedVulnerability)"
                class="px-4 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                :disabled="isSaving"
              >
                Delete
              </button>
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                :disabled="isSaving"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isSaving"
              >
                <span v-if="isSaving" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
                <span v-else>{{ selectedVulnerability ? 'Save Changes' : 'Add Vulnerability' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Confirm Delete Vulnerability"
      message="Are you sure you want to delete this vulnerability? This action cannot be undone."
      :loading="isDeleting"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import DataTable from '@/components/DataTable.vue'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'

// Severity → default SLA in days. Mirrors back/internal/db/models/vulns.go
// RemediationSLAForSeverity — kept in sync so the UI hint matches backend reality.
const SLA_DAYS = { CRITICAL: 7, HIGH: 30, MEDIUM: 90, LOW: 180 }

export default {
  name: 'VulnerabilitiesView',
  components: {
    DataTable,
    DeleteConfirmationModal
  },
  setup() {
    const store = useStore()
    const showModal = ref(false)
    const showDeleteModal = ref(false)
    const selectedVulnerability = ref(null)
    const vulnerabilityToDelete = ref(null)
    const isSaving = ref(false)
    const isDeleting = ref(false)
    const products = ref([])

    const getAPIURL = () => {
      const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
      const cleanUrl = envUrl.replace(/\/$/, '')
      return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
    }

    const exportVDR = async () => {
      try {
        const tenantId = store.state.currentTenantId
        if (!tenantId) return
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId}/vdr.cdx.json`, {
          headers: { Authorization: `Bearer ${store.state.token}` },
          responseType: 'blob'
        })
        const blob = new Blob([res.data], { type: 'application/vnd.cyclonedx+json' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `vdr-${tenantId}.cdx.json`
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error('Failed to export VDR:', e)
      }
    }

    const fetchProducts = async () => {
      try {
        const tenantId = store.state.currentTenantId
        if (!tenantId) return
        const response = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId}/products`, {
          headers: { Authorization: `Bearer ${store.state.token}` },
          params: { per_page: 100 }
        })
        products.value = response.data?.data || response.data || []
      } catch (e) {
        console.error('Failed to fetch products:', e)
      }
    }

    const isReporting = ref(false)
    const availableVersions = ref([])
    const versionSearch = ref('')

    const filteredVersions = computed(() => {
      if (!versionSearch.value) return availableVersions.value
      const q = versionSearch.value.toLowerCase()
      return availableVersions.value.filter(v => v.version.toLowerCase().includes(q))
    })

    const fetchVersionsForProduct = async (productId) => {
      if (!productId) { availableVersions.value = []; return }
      try {
        const tenantId = store.state.currentTenantId
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId}/products/${productId}/versions`, {
          headers: { Authorization: `Bearer ${store.state.token}` },
          params: { per_page: 500 }
        })
        availableVersions.value = res.data?.data || res.data || []
      } catch (e) {
        console.error('Failed to fetch versions:', e)
        availableVersions.value = []
      }
    }

    const onProductChange = () => {
      form.value.affected_version_ids = []
      versionSearch.value = ''
      fetchVersionsForProduct(form.value.product_id)
    }

    const emptyForm = () => ({
      product_id: null,
      cve_id: '',
      synopsis: '',
      description: '',
      severity: 'MEDIUM',
      status: 'OPEN',
      fixed_version: '',
      cwe: '',
      cvss: '',
      cvss_score: 0,
      affected_components: '',
      is_public: false,
      due_by: '',
      advisory_status: '',
      publish_at: '',
      affected_version_ids: []
    })

    const form = ref(emptyForm())

    const columns = [
      { key: 'cve_id', label: 'CVE / ID', sortable: true, formatter: (v) => v || '—' },
      { key: 'synopsis', label: 'Synopsis', sortable: true },
      { key: 'severity', label: 'Severity', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'due_by', label: 'Due By', sortable: true },
      { key: 'advisory_status', label: 'Advisory', sortable: true }
    ]

    // Helpers for date handling — ISO-date-only for <input type=date>,
    // full ISO for datetime-local. Both backend fields accept either.
    const toDateInput = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    }
    const toDateTimeInput = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
    const formatDate = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString()
    }
    const isDatePast = (iso) => {
      if (!iso) return false
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return false
      return d.getTime() < Date.now()
    }
    const daysUntil = (iso) => {
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return 0
      return Math.ceil((d.getTime() - Date.now()) / 86400000)
    }
    const slaDefaultDays = computed(() => SLA_DAYS[form.value.severity] || 90)

    const showVulnerabilityDetails = (vulnerability) => {
      selectedVulnerability.value = vulnerability
      form.value = {
        product_id: vulnerability.product_id || null,
        cve_id: vulnerability.cve_id || '',
        synopsis: vulnerability.synopsis,
        description: vulnerability.description,
        severity: vulnerability.severity,
        status: vulnerability.status,
        fixed_version: vulnerability.fixed_version || '',
        cwe: vulnerability.cwe || '',
        cvss: vulnerability.cvss || '',
        cvss_score: vulnerability.cvss_score || 0,
        affected_components: vulnerability.affected_components || '',
        is_public: vulnerability.is_public !== undefined ? vulnerability.is_public : false,
        due_by: toDateInput(vulnerability.due_by),
        advisory_status: vulnerability.advisory_status || '',
        publish_at: toDateTimeInput(vulnerability.publish_at),
        affected_version_ids: (vulnerability.affected_versions || []).map(v => v.id)
      }
      versionSearch.value = ''
      if (vulnerability.product_id) fetchVersionsForProduct(vulnerability.product_id)
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      selectedVulnerability.value = null
      form.value = emptyForm()
      availableVersions.value = []
      versionSearch.value = ''
    }

    const toggleVulnVisibility = async () => {
      if (!selectedVulnerability.value) return
      form.value.is_public = !form.value.is_public
      try {
        await store.dispatch('vulnerabilities/updateData', {
          tenantId: store.state.currentTenantId,
          id: selectedVulnerability.value.id,
          data: { is_public: form.value.is_public }
        })
        selectedVulnerability.value.is_public = form.value.is_public
        store.dispatch('notification/showNotification', { type: 'success', message: form.value.is_public ? 'Now public' : 'Now private', duration: 2000 })
      } catch {
        form.value.is_public = !form.value.is_public
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to update visibility' })
      }
    }

    const handleSubmit = async () => {
      try {
        isSaving.value = true
        const data = {
          cve_id: form.value.cve_id || '',
          synopsis: form.value.synopsis,
          description: form.value.description,
          severity: form.value.severity,
          status: form.value.status,
          fixed_version: form.value.fixed_version || '',
          product_id: form.value.product_id || undefined,
          cwe: form.value.cwe || undefined,
          cvss: form.value.cvss || undefined,
          cvss_score: form.value.cvss_score || undefined,
          affected_components: form.value.affected_components || undefined,
          is_public: form.value.is_public,
          affected_version_ids: form.value.affected_version_ids.length > 0 ? form.value.affected_version_ids : undefined
        }

        // SLA + advisory — only include when set so the backend can compute
        // defaults on create (e.g. due_by from severity).
        if (form.value.due_by) {
          // Send as full ISO so Go's time.Time unmarshaller accepts it uniformly.
          data.due_by = new Date(form.value.due_by + 'T00:00:00Z').toISOString()
        }
        if (form.value.advisory_status) {
          data.advisory_status = form.value.advisory_status
        }
        if (form.value.advisory_status === 'scheduled' && form.value.publish_at) {
          data.publish_at = new Date(form.value.publish_at).toISOString()
        }

        if (selectedVulnerability.value) {
          await store.dispatch('vulnerabilities/updateData', {
            tenantId: store.state.currentTenantId,
            id: selectedVulnerability.value.id,
            data
          })
        } else {
          await store.dispatch('vulnerabilities/createData', {
            tenantId: store.state.currentTenantId,
            data
          })
        }

        // Refresh the data after successful save
        await store.dispatch('vulnerabilities/fetchData', {
          tenantId: store.state.currentTenantId,
          page: 1,
          entriesPerPage: 10,
          search: '',
          orderBy: 'id',
          sortDirection: 'asc'
        })

        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'Vulnerability ' + (selectedVulnerability.value ? 'updated' : 'created') + ' successfully',
          duration: 3000
        })

        closeModal()
      } catch (error) {
        console.error('Error saving vulnerability:', error)
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.response?.data?.message || 'Failed to ' + (selectedVulnerability.value ? 'update' : 'create') + ' vulnerability',
          duration: 5000
        })
      } finally {
        isSaving.value = false
      }
    }

    const deleteVulnerability = (vulnerability) => {
      vulnerabilityToDelete.value = vulnerability
      showDeleteModal.value = true
    }

    const handleDeleteConfirm = async () => {
      if (!vulnerabilityToDelete.value) return

      try {
        isDeleting.value = true
        await store.dispatch('vulnerabilities/deleteData', {
          tenantId: store.state.currentTenantId,
          id: vulnerabilityToDelete.value.id
        })

        // Refresh the data after successful deletion
        await store.dispatch('vulnerabilities/fetchData', {
          tenantId: store.state.currentTenantId,
          page: 1,
          entriesPerPage: 10,
          search: '',
          orderBy: 'id',
          sortDirection: 'asc'
        })

        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'Vulnerability deleted successfully',
          duration: 3000
        })

        closeModal()
        showDeleteModal.value = false
        vulnerabilityToDelete.value = null
      } catch (error) {
        console.error('Error deleting vulnerability:', error)
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.response?.data?.message || 'Failed to delete vulnerability',
          duration: 5000
        })
      } finally {
        isDeleting.value = false
      }
    }

    const reportToENISA = async (vulnerability) => {
      isReporting.value = true
      try {
        const tenantId = store.state.currentTenantId
        await axios.post(
          `${getAPIURL()}/admin/tenants/${tenantId}/vulns/${vulnerability.id}/report-incident`,
          {},
          { headers: { Authorization: `Bearer ${store.state.token}` } }
        )
        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'ENISA incident created from vulnerability — go to Incidents to manage it',
          duration: 5000
        })
        closeModal()
      } catch (error) {
        const msg = error.response?.data?.error || 'Failed to create incident'
        store.dispatch('notification/showNotification', { type: 'error', message: msg, duration: 5000 })
      } finally {
        isReporting.value = false
      }
    }

    onMounted(() => {
      fetchProducts()
    })

    return {
      columns,
      showModal,
      showDeleteModal,
      selectedVulnerability,
      vulnerabilityToDelete,
      form,
      isSaving,
      isDeleting,
      products,
      availableVersions,
      versionSearch,
      filteredVersions,
      showVulnerabilityDetails,
      closeModal,
      handleSubmit,
      deleteVulnerability,
      handleDeleteConfirm,
      reportToENISA,
      isReporting,
      toggleVulnVisibility,
      onProductChange,
      exportVDR,
      formatDate,
      isDatePast,
      daysUntil,
      slaDefaultDays
    }
  }
}
</script>
